/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import * as _ from 'lodash';
import {Observable, throwError} from 'rxjs';

import {ApiConfig} from '../../config/api-config';
import {CHUNK_SIZE} from '../constants/resource-constants';
import {AdnApiClientService} from './adn-api-client.service';

export interface StatsParams {
  id?: string;
  idKey?: string;
  startDate?: any;
  endDate?: any;
  groupBy?: string | null;
  statTypeId?: string;
  [key: string]: any;
}

@Injectable({providedIn: 'root'})
export class StatsApiService {
  private readonly statsUri = this.apiConfig.getVersionedUri('stats');
  private readonly statsTrafficUri = this.apiConfig.getVersionedUri('stats/traffic');
  private readonly statsLocationUri = this.apiConfig.getVersionedUri('stats/location');
  private readonly statsDomainUri = this.apiConfig.getVersionedUri('stats/domain');
  private readonly statsTargetingUri = this.apiConfig.getVersionedUri('stats/targeting');
  private readonly statsArticlesUri = this.apiConfig.getVersionedUri('stats/article');
  private readonly predictTrafficUri = this.apiConfig.getVersionedUri('stats/predict');
  private readonly cdnStatsUri = this.apiConfig.getVersionedUri('stats/cdn');
  private readonly co2StatsUri = this.apiConfig.getVersionedUri('stats/co2');
  private readonly defaultParams = {
    showFilteredBidReasons: true,
    groupBy: CHUNK_SIZE.DAILY.key
  };

  constructor(private readonly client: AdnApiClientService, private readonly apiConfig: ApiConfig) {
  }

  getPredict(params: StatsParams): Observable<any> {
    return this.request(this.predictTrafficUri, params);
  }

  getCdn(params: StatsParams): Observable<any> {
    return this.request(this.cdnStatsUri, params);
  }

  getCo2(params: StatsParams): Observable<any> {
    return this.request(this.co2StatsUri, params);
  }

  getTraffic(params: StatsParams): Observable<any> {
    return this.request(this.statsTrafficUri + '/' + params.statTypeId, params);
  }

  getLocation(params: StatsParams): Observable<any> {
    return this.request(this.statsLocationUri + '/' + params.statTypeId, params);
  }

  getDomain(params: StatsParams): Observable<any> {
    const suffix = params.statTypeId ? '/' + params.statTypeId : '';
    return this.request(this.statsDomainUri + suffix, suffix ? params : {...params, groupBy: 'DOMAIN_NAME'});
  }

  getTargeting(params: StatsParams): Observable<any> {
    return this.request(this.statsTargetingUri, params);
  }

  getArticles(params: StatsParams): Observable<any> {
    return this.request(this.statsArticlesUri, params);
  }

  getAsync(params: StatsParams): Observable<any> {
    const asyncSuffix = params?.id ? '/' + params.id : '';
    return this.request(this.statsUri + '/async' + asyncSuffix, params);
  }

  getAsyncTargeting(params: StatsParams): Observable<any> {
    return this.request(this.statsUri + '/async/targeting', params);
  }

  get(params: StatsParams): Observable<any> {
    return this.request(this.statsUri, params);
  }

  private request(url: string, params: StatsParams): Observable<any> {
    const requestParams = _.assign({}, this.defaultParams, params);
    if (requestParams.startDate?.toISOString) {
      requestParams.startDate = requestParams.startDate.toISOString();
    }
    if (requestParams.endDate?.toISOString) {
      requestParams.endDate = requestParams.endDate.toISOString();
    }
    if (requestParams.idKey) {
      requestParams[requestParams.idKey] = requestParams.id;
    }
    if (!requestParams.groupBy) {
      delete requestParams.groupBy;
    } else {
      requestParams.groupBy = requestParams.groupBy.toUpperCase();
    }
    requestParams.includeChunkRange = true;

    if (!requestParams.startDate && _.get(requestParams, [requestParams.idKey], '').indexOf(';') > -1) {
      return throwError(() => ({invalidDate: true}));
    }

    return this.client.request({
      method: 'GET',
      url: url + '/',
      params: _.omit(requestParams, ['id', 'idKey']),
      hasErrorHandler: true
    });
  }
}
